import { MouseEvent, useState } from 'react';

import {
  analyticsBulkCancel,
  analyticsSelectedDeselectedAllRidesOnDay,
  analyticsSelectedDeselectedSingleRide,
} from '../../../api/services/Analytics';
import { bulkCancelRidesByDates } from '../../../api/services/Rides';
import { ITEM_LIMIT_PER_PAGE } from '../../../constants';
import { useTimezone } from '../../../hooks/useTimezone';
import { useToastStore } from '../../../shared/stores/toastStore';

type GroupCancel = {
  label: string;
  date: Date;
  all: boolean;
  except: number[];
  only: number[];
};

interface IUseBulkCancelRides {
  selectedRideGroups: GroupCancel[];
  selectedGroups: string[];
  numberSelectedRides: number;
  handleCheckboxSelection: (
    event: MouseEvent,
    id: number,
    ridesCount: number,
    startsAt: Date,
    groupKey: string
  ) => void;
  handleAllSelection: (startsAt: Date, group: string) => void;
  handleCancelRides: ({ cancellationReasonKey }) => void;
  cancelRidesResponse: string;
  cancelSuccessCount: number;
  reset: () => void;
  isLoading: boolean;
}

export const useBulkCancelRides = (state: string): IUseBulkCancelRides => {
  const [cancelRidesResponse, setCancelRidesResponse] = useState<string>('');
  const [cancelSuccessCount, setCancelSuccessCount] = useState(0);
  const { addToast, resetToasts } = useToastStore();
  const [selectedRideGroups, setSelectedRideGroups] = useState<GroupCancel[]>([]);
  const { getDateInTimezone, getStartOfDate } = useTimezone();
  const selectedGroups = selectedRideGroups.map((group) => group.label);
  const [numberSelectedRides, setNumberSelectedRides] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handlePartialCancelResults = async (selectedRides) => {
    setIsLoading(true);
    const response = await bulkCancelRidesByDates({
      cancel: formatCancelRides(selectedRides),
      cancellationReasonKey: '',
      confirm: false,
      type: state,
    });

    if (response?.type === 'success') {
      const numberRideMatches = parseInt(response.data.message.match(/\d+/)?.join('') || '0');

      setNumberSelectedRides(numberRideMatches);

      if (numberRideMatches > ITEM_LIMIT_PER_PAGE) {
        resetToasts();
        addToast({
          severity: 'info',
          message: `${numberRideMatches} rides that meet these search criteria have been selected. Some rides may not be visible on this screen to improve performance.`,
        });
      }

      const selectType = numberSelectedRides < numberRideMatches ? 'Selected' : 'Deselected';
      const quantity =
        selectType === 'Selected' ? numberRideMatches - numberSelectedRides : numberSelectedRides - numberRideMatches;

      analyticsSelectedDeselectedAllRidesOnDay(selectType, quantity);
    } else {
      response.error.errors.forEach((error) => addToast({ severity: 'error', message: error }));
    }
    setIsLoading(false);
  };

  const getUpdatedAllGroup = (group, ridesCount, rideId) => {
    const groupIndex = selectedGroups.indexOf(group.label);

    // remove group if except length matches rides count
    if (group.except.indexOf(rideId) === -1 && group.except.length + 1 === ridesCount) {
      return selectedRideGroups.filter((_, index) => index !== groupIndex);
    }
    // re-selecting a ride from a select all group
    if (group.except.indexOf(rideId) !== -1) {
      setNumberSelectedRides(numberSelectedRides + 1);

      return [
        ...selectedRideGroups.slice(0, groupIndex),
        {
          ...group,
          except: group.except.filter((exceptId) => exceptId !== rideId),
        },
        ...selectedRideGroups.slice(groupIndex + 1),
      ];
    }
    setNumberSelectedRides(numberSelectedRides - 1);

    // deselecting a ride from a select all group
    return [
      ...selectedRideGroups.slice(0, groupIndex),
      {
        ...group,
        except: [...group.except, rideId],
      },
      ...selectedRideGroups.slice(groupIndex + 1),
    ];
  };

  const getUpdatedOnlyGroup = (group, rideId) => {
    const groupIndex = selectedGroups.indexOf(group.label);

    // remove group if last only ride
    if (group.only.indexOf(rideId) !== -1 && group.only.length === 1) {
      setNumberSelectedRides(numberSelectedRides - 1);
      analyticsSelectedDeselectedSingleRide('Deselected');

      return selectedRideGroups.filter((_, index) => index !== groupIndex);
    }

    // deselecting a ride from a select only group
    if (group.only.indexOf(rideId) !== -1) {
      setNumberSelectedRides(numberSelectedRides - 1);
      analyticsSelectedDeselectedSingleRide('Deselected');

      return [
        ...selectedRideGroups.slice(0, groupIndex),
        {
          ...group,
          only: group.only.filter((onlyId) => onlyId !== rideId),
        },
        ...selectedRideGroups.slice(groupIndex + 1),
      ];
    }
    setNumberSelectedRides(numberSelectedRides + 1);
    analyticsSelectedDeselectedSingleRide('Selected');

    // re-selecting a ride from a select only  group
    return [
      ...selectedRideGroups.slice(0, groupIndex),
      {
        ...group,
        only: [...group.only, rideId],
      },
      ...selectedRideGroups.slice(groupIndex + 1),
    ];
  };

  const handleAllSelection = (startsAt: Date, group) => {
    const isSelectedGroup = selectedGroups.indexOf(group) !== -1;
    const groupDate = getStartOfDate(getDateInTimezone(startsAt));
    let updatedSelectedRideGroups;

    if (isSelectedGroup) {
      updatedSelectedRideGroups = selectedRideGroups.filter((selectedRideGroup) => selectedRideGroup.label !== group);
    } else {
      const newGroup = {
        label: group,
        date: groupDate,
        all: true,
        except: [],
        only: [],
      };

      updatedSelectedRideGroups = [...selectedRideGroups, newGroup];
    }

    setSelectedRideGroups(updatedSelectedRideGroups);

    updatedSelectedRideGroups.length ? handlePartialCancelResults(updatedSelectedRideGroups) : handleDeselectedAll();
  };

  const handleDeselectedAll = () => {
    analyticsSelectedDeselectedAllRidesOnDay('Deselected', numberSelectedRides);
    setNumberSelectedRides(0);
  };

  const handleCheckboxSelection = (
    event: MouseEvent,
    id: number,
    ridesCount: number,
    startsAt: Date,
    groupKey: string
  ) => {
    event.stopPropagation();

    const groupFound = selectedRideGroups.find((rideGroup) => rideGroup.label === groupKey);
    let updatedGroups;

    if (groupFound) {
      if (groupFound.all) {
        updatedGroups = getUpdatedAllGroup(groupFound, ridesCount, id);

        updatedGroups.length < selectedRideGroups.length && handleAllSelection(startsAt, groupKey);
      } else {
        updatedGroups = getUpdatedOnlyGroup(groupFound, id);
      }
    } else {
      // create group for individually selected ride
      const groupDate = getStartOfDate(getDateInTimezone(startsAt));

      updatedGroups = [
        ...selectedRideGroups,
        {
          label: groupKey,
          date: groupDate,
          all: false,
          except: [],
          only: [id],
        },
      ];

      setNumberSelectedRides(numberSelectedRides + 1);
      analyticsSelectedDeselectedSingleRide('Selected');
    }
    setSelectedRideGroups(updatedGroups);
  };

  const handleCancelRides = async ({ cancellationReasonKey }) => {
    if (selectedRideGroups.length) {
      setIsLoading(true);
      const response = await bulkCancelRidesByDates({
        cancel: formatCancelRides(selectedRideGroups),
        cancellationReasonKey: cancellationReasonKey,
        confirm: true,
        type: state,
      });

      if (response?.type === 'success') {
        setCancelRidesResponse(response.data.message);
        setCancelSuccessCount((count) => count + 1);
        analyticsBulkCancel(numberSelectedRides);
      } else {
        response.error.errors.forEach((error) => addToast({ severity: 'error', message: error }));
      }
      setIsLoading(false);
    }
  };

  const reset = () => {
    setSelectedRideGroups([]);
    setCancelRidesResponse('');
    setNumberSelectedRides(0);
  };

  return {
    selectedRideGroups,
    selectedGroups,
    numberSelectedRides,
    handleCheckboxSelection,
    handleAllSelection,
    handleCancelRides,
    cancelRidesResponse,
    cancelSuccessCount,
    reset,
    isLoading,
  };
};

const formatCancelRides = (selectedGroupRides) => {
  return selectedGroupRides.map((selectedGroup) =>
    selectedGroup.all && selectedGroup.except.length ? { ...selectedGroup, all: false } : selectedGroup
  );
};
